export type BoardLeaseMode = "checking" | "writer" | "read_only";

export interface BoardMessageChannel {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent<unknown>) => void): void;
  removeEventListener(type: "message", listener: (event: MessageEvent<unknown>) => void): void;
  close(): void;
}

export interface BoardLeaseEnvironment {
  tryAcquire(name: string, hold: () => Promise<void>): Promise<boolean>;
  createChannel(name: string): BoardMessageChannel;
}

export interface BoardLease {
  setRelinquishHandler(handler: () => Promise<void>): void;
  start(): Promise<BoardLeaseMode>;
  takeOver(): Promise<boolean>;
  dispose(): void;
}

export interface BoardMigrationLease {
  run<T>(key: string, operation: () => Promise<T>): Promise<T>;
}

type BoardLeaseMessage =
  | { readonly type: "takeover"; readonly boardKey: string; readonly requestId: string }
  | { readonly type: "released" | "denied"; readonly boardKey: string; readonly requestId: string };

const CHANNEL_NAME = "shotboard-board-leases";
const TAKEOVER_WAIT_MILLISECONDS = 750;
const ACQUIRE_RETRY_MILLISECONDS = 20;
const ACQUIRE_RETRY_COUNT = 5;

/** Produces the single lock namespace shared by migrations and editor leases. */
function boardLockName(key: string): string {
  return `shotboard:${key}`;
}

/** Serializes migration rewrites with the same browser lock used by active editors. */
export class BrowserBoardMigrationLease implements BoardMigrationLease {
  /** Waits for exclusive ownership before executing one bounded migration transaction. */
  public run<T>(key: string, operation: () => Promise<T>): Promise<T> {
    return navigator.locks.request(boardLockName(key), { mode: "exclusive" }, operation);
  }
}

/** Adapts Web Locks and BroadcastChannel into a testable board-lease environment. */
export class BrowserBoardLeaseEnvironment implements BoardLeaseEnvironment {
  /** Attempts one non-blocking exclusive Web Lock and holds it until the callback completes. */
  public async tryAcquire(name: string, hold: () => Promise<void>): Promise<boolean> {
    let resolveAvailability: (available: boolean) => void = () => undefined;
    let rejectAvailability: (error: unknown) => void = () => undefined;
    let availabilitySettled = false;
    const availability = new Promise<boolean>((resolve, reject) => {
      resolveAvailability = resolve;
      rejectAvailability = reject;
    });
    void navigator.locks
      .request(name, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        availabilitySettled = true;
        if (lock === null) {
          resolveAvailability(false);
          return;
        }
        resolveAvailability(true);
        await hold();
      })
      .catch((error: unknown) => {
        if (!availabilitySettled) {
          rejectAvailability(error);
        }
      });
    return availability;
  }

  /** Opens the one origin-scoped coordination channel shared by Shotboard tabs. */
  public createChannel(name: string): BoardMessageChannel {
    return new BroadcastChannel(name);
  }
}

/** Holds one per-board writer lease and coordinates explicit cross-tab takeover. */
export class BoardLeaseCoordinator implements BoardLease {
  private readonly boardKey: string;
  private readonly environment: BoardLeaseEnvironment;
  private readonly channel: BoardMessageChannel;
  private readonly onMode: (mode: BoardLeaseMode) => void;
  private mode: BoardLeaseMode = "checking";
  private releaseHold: (() => void) | null = null;
  private disposed = false;
  private relinquish: () => Promise<void> = async () => undefined;

  /** Creates one coordinator without acquiring a lock before its owning effect starts. */
  public constructor(
    boardKey: string,
    onMode: (mode: BoardLeaseMode) => void,
    environment: BoardLeaseEnvironment = new BrowserBoardLeaseEnvironment(),
  ) {
    this.boardKey = boardKey;
    this.onMode = onMode;
    this.environment = environment;
    this.channel = environment.createChannel(CHANNEL_NAME);
    this.channel.addEventListener("message", this.handleMessage);
  }

  /** Installs the current editor's flush callback before another tab can take over. */
  public setRelinquishHandler(handler: () => Promise<void>): void {
    this.relinquish = handler;
  }

  /** Attempts the initial non-blocking writer acquisition. */
  public async start(): Promise<BoardLeaseMode> {
    if (this.disposed) {
      return "read_only";
    }
    const acquired = await this.environment.tryAcquire(this.lockName(), () => this.hold());
    if (this.disposed) {
      return "read_only";
    }
    this.setMode(acquired ? "writer" : "read_only");
    return this.mode;
  }

  /** Requests the current writer to flush and release before attempting acquisition. */
  public async takeOver(): Promise<boolean> {
    if (this.mode === "writer") {
      return true;
    }
    if (this.disposed) {
      return false;
    }
    if ((await this.start()) === "writer") {
      return true;
    }
    const requestId = crypto.randomUUID();
    const response = this.waitForResponse(requestId);
    this.channel.postMessage({ type: "takeover", boardKey: this.boardKey, requestId });
    const released = await response;
    if (!released || this.disposed) {
      return false;
    }
    for (let attempt = 0; attempt < ACQUIRE_RETRY_COUNT; attempt += 1) {
      if ((await this.start()) === "writer") {
        return true;
      }
      await delay(ACQUIRE_RETRY_MILLISECONDS);
    }
    return false;
  }

  /** Releases the current lock and closes only this tab's coordination channel. */
  public dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.releaseHold?.();
    this.releaseHold = null;
    this.channel.removeEventListener("message", this.handleMessage);
    this.channel.close();
  }

  /** Holds the Web Lock until disposal or an accepted takeover request. */
  private hold(): Promise<void> {
    if (this.disposed) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.releaseHold = resolve;
    });
  }

  /** Handles only validated messages scoped to this board identity. */
  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    const message = parseBoardLeaseMessage(event.data);
    if (message === null || message.boardKey !== this.boardKey || message.type !== "takeover") {
      return;
    }
    if (this.mode !== "writer") {
      return;
    }
    void this.handleTakeover(message.requestId);
  };

  /** Flushes the current writer before releasing or explicitly denying takeover. */
  private async handleTakeover(requestId: string): Promise<void> {
    try {
      await this.relinquish();
      this.releaseHold?.();
      this.releaseHold = null;
      this.setMode("read_only");
      this.channel.postMessage({ type: "released", boardKey: this.boardKey, requestId });
    } catch {
      this.channel.postMessage({ type: "denied", boardKey: this.boardKey, requestId });
    }
  }

  /** Waits for one matching release response without trusting arbitrary channel payloads. */
  private waitForResponse(requestId: string): Promise<boolean> {
    return new Promise((resolve) => {
      let timeout = 0;
      /** Resolves only the matching request and removes both completion paths. */
      const listener = (event: MessageEvent<unknown>): void => {
        const message = parseBoardLeaseMessage(event.data);
        if (
          message === null ||
          message.boardKey !== this.boardKey ||
          message.requestId !== requestId ||
          message.type === "takeover"
        ) {
          return;
        }
        window.clearTimeout(timeout);
        this.channel.removeEventListener("message", listener);
        resolve(message.type === "released");
      };
      this.channel.addEventListener("message", listener);
      timeout = window.setTimeout(() => {
        this.channel.removeEventListener("message", listener);
        resolve(true);
      }, TAKEOVER_WAIT_MILLISECONDS);
    });
  }

  /** Publishes meaningful lease changes without duplicate announcements. */
  private setMode(mode: BoardLeaseMode): void {
    if (this.mode === mode) {
      return;
    }
    this.mode = mode;
    this.onMode(mode);
  }

  /** Names one origin lock by workspace-scoped stable board identity. */
  private lockName(): string {
    return boardLockName(this.boardKey);
  }
}

/** Narrows untrusted BroadcastChannel data to the minimal lease protocol. */
function parseBoardLeaseMessage(value: unknown): BoardLeaseMessage | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const record = Object.fromEntries(Object.entries(value));
  if (
    (record.type !== "takeover" && record.type !== "released" && record.type !== "denied") ||
    typeof record.boardKey !== "string" ||
    typeof record.requestId !== "string"
  ) {
    return null;
  }
  return { type: record.type, boardKey: record.boardKey, requestId: record.requestId };
}

/** Defers a retry so the browser can complete the previous Web Lock callback. */
function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
